/**
 * Phase 8 · Cluster C-48 — Route-scoped agent quick actions.
 *
 * Spec: docs/ux/PHASE8_WRANGLE_TRANSFORMATION.md §34.2.B
 *
 * Each route pattern maps to a small, fixed set of agent "chips" rendered
 * above the composer. Clicking a chip fills the composer with a templated
 * prompt substituted with fields from the currently resolved entity.
 *
 * `requiresEntity: true` chips are hidden when the route does not resolve
 * an entity (e.g. before the detail page finishes loading).
 *
 * Template placeholders:
 *   {id}         — entity.id (raw)
 *   {label}      — entity.label
 *   {kind}       — entity.kind ('requisition' | 'candidate' | …)
 *   {route}      — the react-router path pattern
 *
 * Keep this file dependency-free — it is imported from `useAgentRouteContext`
 * which runs on every route change.
 */

import type { AgentQuickAction } from './types';

export type { AgentQuickAction } from './types';

export const ACTIONS_FOR_ROUTE: Record<string, AgentQuickAction[]> = {
  '/': [
    {
      id: 'home.plan',
      label: "Draft today's plan",
      promptTemplate: "Draft today's plan for me — what should I work on first?",
      requiresEntity: false,
    },
    {
      id: 'home.attention',
      label: 'What needs my attention?',
      promptTemplate:
        'What needs my attention today? Summarise stalled requisitions, overdue follow-ups, and pending approvals.',
      requiresEntity: false,
    },
  ],

  '/requisitions': [
    {
      id: 'requisitions.create',
      label: 'Create a requisition',
      promptTemplate:
        'Create a new requisition. Ask me for the essentials (role title, client, headcount, priority) before drafting.',
      requiresEntity: false,
    },
    {
      id: 'requisitions.at-risk',
      label: 'List at-risk positions',
      promptTemplate: 'List at-risk positions (TAT within 3 days of breach) in this org.',
      requiresEntity: false,
    },
    {
      id: 'requisitions.tat-breaches',
      label: 'Report TAT breaches this week',
      promptTemplate:
        'Report TAT breaches this week — group by owner and show days-over-target for each.',
      requiresEntity: false,
    },
  ],

  '/requisitions/:id': [
    {
      id: 'requisition.shortlist-top5',
      label: 'Shortlist top 5 candidates for this role',
      promptTemplate:
        'Shortlist top 5 candidates for requisition {id} — {label}. Run a search, pick the best 5, add them to this role\'s pipeline, and draft personalised outreach for each.',
      requiresEntity: true,
    },
    {
      id: 'requisition.schedule-intake',
      label: 'Schedule intake interview',
      promptTemplate:
        'Schedule an intake interview with the hiring manager for requisition {id} — {label}.',
      requiresEntity: true,
    },
    {
      id: 'requisition.draft-outreach',
      label: 'Draft outreach sequence',
      promptTemplate:
        'Draft a 3-step outreach sequence for requisition {id} — {label}. Keep it concise and personable.',
      requiresEntity: true,
    },
    {
      id: 'requisition.summarise-apps',
      label: 'Summarise applications so far',
      promptTemplate:
        'Summarise the applications so far for requisition {id} — {label}. Break down by stage and highlight anyone worth a second look.',
      requiresEntity: true,
    },
  ],

  '/candidates': [
    {
      id: 'candidates.find-rust-pune',
      label: 'Find Rust devs in Pune',
      promptTemplate: 'Find senior Rust developers in Pune with 5+ years of experience.',
      requiresEntity: false,
    },
    {
      id: 'candidates.bulk-import-peerlist',
      label: 'Bulk-import from Peerlist',
      promptTemplate:
        'Bulk-import candidates from Peerlist matching my most recent open requisition.',
      requiresEntity: false,
    },
    {
      id: 'candidates.dedupe',
      label: 'Dedupe obvious duplicates',
      promptTemplate:
        'Scan the candidate pool for obvious duplicates (same email / phone / LinkedIn) and propose merges — do not execute any merges yet.',
      requiresEntity: false,
    },
  ],

  '/candidates/:id': [
    {
      id: 'candidate.outreach-email',
      label: 'Write a personalised outreach email',
      promptTemplate:
        'Write a personalised outreach email for candidate {id} — {label}. Reference their current role and skills.',
      requiresEntity: true,
    },
    {
      id: 'candidate.schedule-screening',
      label: 'Schedule a screening',
      promptTemplate:
        'Schedule a 30-minute screening call for candidate {id} — {label} in the next 3 business days.',
      requiresEntity: true,
    },
    {
      id: 'candidate.add-to-pipeline',
      label: 'Add to an open pipeline',
      promptTemplate:
        'Add candidate {id} — {label} to one of my open pipelines. Suggest the best-fit role first.',
      requiresEntity: true,
    },
    {
      id: 'candidate.summarise-fit',
      label: 'Summarise fit vs open roles',
      promptTemplate:
        'Summarise how candidate {id} — {label} fits against my currently open roles. Rank the top 3 matches with rationale.',
      requiresEntity: true,
    },
  ],

  '/applications/:id': [
    {
      id: 'application.advance',
      label: 'Advance to next stage',
      promptTemplate: 'Advance application {id} — {label} to the next stage with a short rationale.',
      requiresEntity: true,
    },
    {
      id: 'application.request-feedback',
      label: 'Request feedback from hiring manager',
      promptTemplate:
        'Draft a concise email to the hiring manager for application {id} — {label} requesting feedback on the latest interview round.',
      requiresEntity: true,
    },
    {
      id: 'application.compose-rejection',
      label: 'Compose rejection',
      promptTemplate:
        'Compose a polite, brand-safe rejection email for application {id} — {label}. Do not send — just draft.',
      requiresEntity: true,
    },
  ],

  '/interviews/:id': [
    {
      id: 'interview.summarise-rate',
      label: 'Summarise and rate',
      promptTemplate:
        'Summarise interview {id} — {label} and give a hire/no-hire rating with a one-line rationale.',
      requiresEntity: true,
    },
    {
      id: 'interview.draft-offer',
      label: 'Draft offer',
      promptTemplate:
        'Draft an offer letter email for interview {id} — {label}. Use the standard org template and leave compensation blank.',
      requiresEntity: true,
    },
    {
      id: 'interview.schedule-followup',
      label: 'Schedule follow-up',
      promptTemplate:
        'Schedule a follow-up interview for {label} in the next 5 business days.',
      requiresEntity: true,
    },
  ],

  '/clients/:id': [
    {
      id: 'client.find-hm-linkedin',
      label: 'Find hiring managers on LinkedIn',
      promptTemplate:
        'Find hiring managers at client {id} — {label} on LinkedIn whose titles suggest they own engineering or product hiring.',
      requiresEntity: true,
    },
    {
      id: 'client.draft-qbr',
      label: 'Draft a QBR',
      promptTemplate:
        'Draft a quarterly business review (QBR) for client {id} — {label}. Include hires this quarter, open reqs, and outstanding issues.',
      requiresEntity: true,
    },
    {
      id: 'client.show-open-reqs',
      label: 'Show open reqs with this client',
      promptTemplate: 'Show the open requisitions for client {id} — {label}, sorted by priority.',
      requiresEntity: true,
    },
  ],

  '/smart-match/*': [
    {
      id: 'smart-match.rerun-fresh',
      label: 'Re-run on fresh JD',
      promptTemplate: 'Re-run the match using the latest version of the job description.',
      requiresEntity: false,
    },
    {
      id: 'smart-match.explain-top1',
      label: 'Explain why #1 was picked',
      promptTemplate:
        'Explain why the #1 candidate was picked in this Smart Match run — cite skills, experience and fit signals.',
      requiresEntity: false,
    },
    {
      id: 'smart-match.save-top10',
      label: 'Save top 10 to pipeline',
      promptTemplate:
        'Shortlist the top 10 candidates from this Smart Match run and add them to the corresponding role\'s pipeline.',
      requiresEntity: false,
    },
  ],
};

/**
 * Substitute template placeholders ({id}, {label}, {kind}, {route}) in a
 * promptTemplate with fields from the supplied entity + route. Unknown
 * placeholders fall through untouched — callers are expected to filter
 * `requiresEntity:true` chips before calling this with a missing entity.
 */
export function renderPromptTemplate(
  template: string,
  opts: {
    route: string;
    entity?: { kind: string; id: string; label: string } | null;
  },
): string {
  const { route, entity } = opts;
  return template
    .replace(/\{id\}/g, entity?.id ?? '')
    .replace(/\{label\}/g, entity?.label ?? '')
    .replace(/\{kind\}/g, entity?.kind ?? '')
    .replace(/\{route\}/g, route)
    .replace(/\s+/g, ' ')
    .trim();
}
